import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useLandingTheme } from '../hooks/useLandingTheme';
import Nav from '../components/common/Nav';
import Footer from '../components/common/Footer';
import Banner from '../components/common/Banner';
import DevNotice from '../components/common/DevNotice';
import CookieConsent from '../components/common/CookieConsent';
import Map from '../components/Home/Map';
import MoreInfo from '../components/Home/MoreInfo';
import Bestseller from '../components/Home/Bestseller';
import Hero from '../components/Home/Hero';
import WhyChooseUs from '../components/Home/WhyChooseUs';
import Discount from '../components/Home/Discount';
import CampaignSection from '../components/Home/CampaignSection';
import WelcomeCampaignModal from '../components/Home/WelcomeCampaignModal';
import { fetchHomepageDiscounts } from '../services/products/productService';
import Text from '../components/Home/Text';
import TopCategoriesBar from '../components/Home/TopCategoriesBar';
import MobileBanner from '../components/Home/MobileBanner';
import HeroDiscountTicker from '../components/Home/HeroDiscountTicker';
import BackdropMotifs from '../components/Home/BackdropMotifs';
import AutumnGuide from '../components/Home/AutumnGuide';
import MedicineCabinet from '../components/Home/MedicineCabinet';

const Home = memo(function Home() {
  useLandingTheme();
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 1030;
    }
    return false;
  });
  const resizeTimeoutRef = useRef(null);

  // Per-campaign sections render above the standalone Discount section.
  // Empty arrays mean nothing renders — the existing Discount component
  // handles the standalone-only side independently.
  const [activeCampaigns, setActiveCampaigns] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchHomepageDiscounts();
        if (!cancelled && r?.success) {
          setActiveCampaigns(r.data?.campaigns || []);
        }
      } catch {
        // soft-fail; standalone section still renders below
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      // Throttle resize handler to reduce re-renders
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        setIsMobileView(window.innerWidth <= 1030);
      }, 150); // Throttle to 150ms
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative max-w-full overflow-x-hidden [overflow-x:clip]">
      {/* Pharmacy figures behind the hero — see BackdropMotifs for why this
          layer is absolute at z-[1] rather than fixed behind the page. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[46rem]"
        style={{
          WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 55%, transparent 95%)',
          maskImage: 'linear-gradient(to bottom, #000 0%, #000 55%, transparent 95%)',
        }}
      >
        <BackdropMotifs />
      </div>

      {/* Film-grain wash. The page is a lot of flat white; a ~2.5% noise layer
          gives it a paper-like texture instead of a screen-flat one, without
          introducing a colour. Fixed and pointer-events-none, and at z-[1] so
          it sits under the nav, modals and every interactive surface. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Welcome popup — surfaces the featured active campaign once per
          campaign. Rendered once here (portal-based dialog, responsive) so it
          covers both the desktop and mobile trees below. */}
      <WelcomeCampaignModal
        campaign={activeCampaigns[0]?.campaign || null}
        products={activeCampaigns[0]?.products || []}
      />

      <div className="hidden lg:block">
        <div className="hidden min-[1121px]:block">
          <Banner />
        </div>
        <div className="block min-[1121px]:hidden">
          <MobileBanner />
        </div>
        <Nav />
        <TopCategoriesBar />
        <DevNotice />
        {/* The Help section lives inside the hero on desktop now (see
            HeroHelpStrip), so it is not repeated here. */}
        <Hero />
        <MedicineCabinet />
        {activeCampaigns.map((entry) => (
          <CampaignSection
            key={entry.campaign._id}
            campaign={entry.campaign}
            products={entry.products}
          />
        ))}
        <AutumnGuide />
        <WhyChooseUs />
        <Text />
        <Map />
        <Footer />
        <CookieConsent />
      </div>

      <div className="block lg:hidden">
        {/* Not wrapped: the nav is sticky on phones, and a wrapper around
            just the banner + nav would be the only box it could stick in. */}
        <MobileBanner />
        <Nav />
        {/* On mobile the ticker sits above the hero, under the nav: the hero
            is nearly a full screen tall here, so below it the band would be
            two scrolls from the top rather than the teaser it is meant to be.
            Desktop puts it under the hero instead. */}
        <DevNotice />
        <Hero />
        <MedicineCabinet />
        <Bestseller />
        {activeCampaigns.map((entry) => (
          <CampaignSection
            key={entry.campaign._id}
            campaign={entry.campaign}
            products={entry.products}
          />
        ))}
        <Discount />
        <AutumnGuide />
        <Map />
        <Footer />
        <CookieConsent />
      </div>
    </div>
  );
});

Home.displayName = 'Home';

export default Home;
